// Advanced Model Training & Email Alerts Component
// Features: Training dashboard, model health, email alert configuration

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Brain, Activity, Zap, RefreshCw, Mail, Bell, CheckCircle2,
  AlertTriangle, TrendingUp, TrendingDown, Settings, Clock,
  Database, Target, ChevronRight, Send, History, Heart
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import { useApiData, useApiMutation } from '@/hooks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
        <p className="text-white font-bold mb-2">{label}</p>
        {payload.map((entry, idx) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Model Health Card
const ModelHealthCard = ({ health, loading }) => {
  if (loading) return <Skeleton className="h-48" />;
  
  const statusColors = {
    healthy: 'from-emerald-500/20 to-green-500/20 border-emerald-500/30',
    needs_attention: 'from-amber-500/20 to-yellow-500/20 border-amber-500/30',
    critical: 'from-rose-500/20 to-red-500/20 border-rose-500/30'
  };
  
  const statusIcons = {
    healthy: <CheckCircle2 className="h-6 w-6 text-emerald-400" />,
    needs_attention: <AlertTriangle className="h-6 w-6 text-amber-400" />,
    critical: <AlertTriangle className="h-6 w-6 text-rose-400" />
  };
  
  return (
    <Card className={`bg-gradient-to-br ${statusColors[health?.status] || statusColors.needs_attention} border`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <Heart className="h-5 w-5 text-rose-400" />
            Model Health
          </CardTitle>
          {statusIcons[health?.status]}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-slate-300 text-sm">Health Score</span>
              <span className="text-white font-bold">{health?.health_score || 0}%</span>
            </div>
            <Progress value={health?.health_score || 0} className="h-2" />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg bg-slate-800/50">
              <p className="text-xs text-slate-400">Training Sessions</p>
              <p className="text-lg font-bold text-white">{health?.total_training_sessions || 0}</p>
            </div>
            <div className="p-3 rounded-lg bg-slate-800/50">
              <p className="text-xs text-slate-400">Predictions Made</p>
              <p className="text-lg font-bold text-white">{health?.total_predictions_recorded || 0}</p>
            </div>
          </div>
          
          {health?.issues?.length > 0 && (
            <div className="space-y-1">
              {health.issues.map((issue, idx) => (
                <p key={idx} className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {issue}
                </p>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Training Panel
const TrainingPanel = ({ token, onTrainingComplete }) => {
  const [isTraining, setIsTraining] = useState(false);
  const [trainingResult, setTrainingResult] = useState(null);
  
  const { data: trainingHistory, loading: historyLoading, refetch: refetchHistory } = useApiData(
    '/api/labor-utilization/advanced-ai/training-history?limit=5'
  );
  
  const runTraining = async () => {
    setIsTraining(true);
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/advanced-ai/run-training?weeks_of_data=52`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTrainingResult(data);
        toast.success('Training session completed!');
        refetchHistory();
        onTrainingComplete?.();
      } else {
        toast.error('Training failed');
      }
    } catch (error) {
      toast.error('Training error');
    } finally {
      setIsTraining(false);
    }
  };
  
  const autoTune = async () => {
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/advanced-ai/auto-tune`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.tuned) {
          toast.success('Model auto-tuned successfully!');
        } else {
          toast.info(data.message || 'No tuning needed');
        }
        onTrainingComplete?.();
      }
    } catch (error) {
      toast.error('Auto-tune failed');
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Training Actions */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <Brain className="h-5 w-5 text-violet-400" />
            Model Training
          </CardTitle>
          <CardDescription className="text-slate-400">
            Train the model with historical data to improve prediction accuracy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={runTraining}
              disabled={isTraining}
              className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 gap-2"
            >
              {isTraining ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
              {isTraining ? 'Training...' : 'Run Full Training'}
            </Button>
            
            <Button
              onClick={autoTune}
              variant="outline"
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 gap-2"
            >
              <Zap className="h-4 w-4" />
              Auto-Tune Weights
            </Button>
          </div>
          
          {/* Training Result */}
          {trainingResult && (
            <div className="mt-4 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <h4 className="font-bold text-emerald-400 mb-2">Training Complete</h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Data Points</p>
                  <p className="text-white font-bold">{trainingResult.training_summary?.data_points_processed || 0}</p>
                </div>
                <div>
                  <p className="text-slate-400">Weeks Analyzed</p>
                  <p className="text-white font-bold">{trainingResult.training_summary?.weeks_analyzed || 0}</p>
                </div>
                <div>
                  <p className="text-slate-400">Duration</p>
                  <p className="text-white font-bold">{trainingResult.training_summary?.duration_seconds?.toFixed(1)}s</p>
                </div>
              </div>
              
              {trainingResult.weight_updates?.adjustments_made?.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-slate-400 mb-1">Adjustments Made:</p>
                  {trainingResult.weight_updates.adjustments_made.map((adj, idx) => (
                    <p key={idx} className="text-xs text-emerald-300">• {adj}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Training History */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <History className="h-5 w-5 text-cyan-400" />
            Training History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <Skeleton className="h-32" />
          ) : trainingHistory?.training_sessions?.length > 0 ? (
            <div className="space-y-2">
              {trainingHistory.training_sessions.map((session, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-slate-800/50 flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium text-sm">{session.session_id}</p>
                    <p className="text-xs text-slate-400">
                      {new Date(session.completed_at).toLocaleDateString()} • {session.data_points} data points
                    </p>
                  </div>
                  <Badge className={session.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                    {session.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-4">No training sessions yet</p>
          )}
        </CardContent>
      </Card>
      
      {/* Feature Importance */}
      {trainingResult?.feature_importance && (
        <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-white font-bold flex items-center gap-2">
              <Target className="h-5 w-5 text-amber-400" />
              Feature Importance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(trainingResult.feature_importance)
                .filter(([key]) => key !== 'message')
                .map(([feature, data]) => (
                  <div key={feature}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-slate-300 text-sm capitalize">{feature.replace(/_/g, ' ')}</span>
                      <span className="text-white font-bold">{(data.importance * 100).toFixed(0)}%</span>
                    </div>
                    <Progress value={data.importance * 100} className="h-2" />
                    <p className="text-xs text-slate-500 mt-1">
                      Correlation: {data.correlation} | Trend: {data.trend}
                    </p>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Email Alerts Panel with Configurable Thresholds
const EmailAlertsPanel = ({ token }) => {
  const [sendingDigest, setSendingDigest] = useState(false);
  const [checkingAlerts, setCheckingAlerts] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [fixedHours, setFixedHours] = useState(32);
  const [percentIncrease, setPercentIncrease] = useState(20);
  const [alertsEnabled, setAlertsEnabled] = useState(true);
  
  const { data: alertSettings, refetch: refetchSettings } = useApiData('/api/labor-utilization/alerts/settings');
  const { data: alertHistory, loading: historyLoading, refetch: refetchHistory } = useApiData(
    '/api/labor-utilization/alerts/history?limit=10'
  );
  
  // Update local state when settings load
  React.useEffect(() => {
    if (alertSettings) {
      setFixedHours(alertSettings.thresholds?.fixed_hours?.value || 32);
      setPercentIncrease(alertSettings.thresholds?.percent_increase?.value || 20);
      setAlertsEnabled(alertSettings.enabled !== false);
    }
  }, [alertSettings]);
  
  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/alerts/settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fixed_hours: fixedHours,
          percent_increase: percentIncrease,
          enabled: alertsEnabled
        })
      });
      
      if (res.ok) {
        toast.success('Alert settings saved!');
        setEditMode(false);
        refetchSettings();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      toast.error('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };
  
  const sendWeeklyDigest = async () => {
    setSendingDigest(true);
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/alerts/send-weekly-digest`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Digest sent to ${data.emails_sent} recipients`);
      } else {
        toast.error('Failed to send digest');
      }
    } catch (error) {
      toast.error('Error sending digest');
    } finally {
      setSendingDigest(false);
    }
  };
  
  const checkAndSendAlerts = async () => {
    setCheckingAlerts(true);
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/alerts/check-and-send`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.alerts_triggered) {
          toast.warning(`${data.alerts.length} alert(s) triggered and sent!`);
          refetchHistory();
        } else {
          toast.success('No alerts triggered - all within thresholds');
        }
      }
    } catch (error) {
      toast.error('Error checking alerts');
    } finally {
      setCheckingAlerts(false);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Alert Actions */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <Mail className="h-5 w-5 text-cyan-400" />
            Email Alerts
          </CardTitle>
          <CardDescription className="text-slate-400">
            Send automated alerts when PRN demand exceeds thresholds
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={checkAndSendAlerts}
              disabled={checkingAlerts || !alertsEnabled}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 gap-2"
            >
              {checkingAlerts ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />}
              {checkingAlerts ? 'Checking...' : 'Check & Send Alerts'}
            </Button>
            
            <Button
              onClick={sendWeeklyDigest}
              disabled={sendingDigest}
              variant="outline"
              className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 gap-2"
            >
              {sendingDigest ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Weekly Digest Now
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Configurable Alert Thresholds */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white font-bold flex items-center gap-2">
              <Settings className="h-5 w-5 text-violet-400" />
              Alert Thresholds
              {!alertSettings?.configured && (
                <Badge className="bg-amber-500/20 text-amber-400 ml-2">Using Defaults</Badge>
              )}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditMode(!editMode)}
              className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10"
            >
              {editMode ? 'Cancel' : 'Configure'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {editMode ? (
            <div className="space-y-6">
              {/* Alerts Enabled Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-slate-800/50">
                <div>
                  <p className="text-white font-medium">Enable Alerts</p>
                  <p className="text-xs text-slate-400">Turn on/off all PRN demand alerts</p>
                </div>
                <Button
                  variant={alertsEnabled ? "default" : "outline"}
                  size="sm"
                  onClick={() => setAlertsEnabled(!alertsEnabled)}
                  className={alertsEnabled ? "bg-emerald-500 hover:bg-emerald-600" : ""}
                >
                  {alertsEnabled ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              
              {/* Fixed Hours Threshold */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-white font-medium">Fixed Hours Threshold</label>
                  <span className="text-amber-400 font-bold text-lg">&gt;{fixedHours} hrs/wk</span>
                </div>
                <input
                  type="range"
                  min="8"
                  max="100"
                  step="8"
                  value={fixedHours}
                  onChange={(e) => setFixedHours(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-amber-500"
                />
                <p className="text-xs text-slate-400">Alert when predicted PRN hours exceed this threshold</p>
              </div>
              
              {/* Percent Increase Threshold */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <label className="text-white font-medium">Percent Increase Threshold</label>
                  <span className="text-rose-400 font-bold text-lg">&gt;{percentIncrease}%</span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="5"
                  value={percentIncrease}
                  onChange={(e) => setPercentIncrease(parseInt(e.target.value))}
                  className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-rose-500"
                />
                <p className="text-xs text-slate-400">Alert when hours increase by this percentage from baseline</p>
              </div>
              
              {/* Save Button */}
              <Button
                onClick={saveSettings}
                disabled={savingSettings}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700"
              >
                {savingSettings ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save Threshold Settings
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <p className="text-amber-400 font-bold mb-1">Fixed Hours Threshold</p>
                  <p className="text-3xl font-black text-white">&gt;{alertSettings?.thresholds?.fixed_hours?.value || 32} hrs/wk</p>
                  <p className="text-xs text-slate-400 mt-2">{alertSettings?.thresholds?.fixed_hours?.description}</p>
                </div>
                
                <div className="p-4 rounded-lg bg-rose-500/10 border border-rose-500/30">
                  <p className="text-rose-400 font-bold mb-1">Percent Increase Threshold</p>
                  <p className="text-3xl font-black text-white">&gt;{alertSettings?.thresholds?.percent_increase?.value || 20}%</p>
                  <p className="text-xs text-slate-400 mt-2">{alertSettings?.thresholds?.percent_increase?.description}</p>
                </div>
              </div>
              
              <div className="mt-4 p-3 rounded-lg bg-slate-800/50">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`h-2 w-2 rounded-full ${alertsEnabled ? 'bg-emerald-400' : 'bg-rose-400'}`}></span>
                  <p className="text-sm text-slate-300">
                    <strong>Status:</strong> {alertsEnabled ? 'Alerts Enabled' : 'Alerts Disabled'}
                  </p>
                </div>
                <p className="text-sm text-slate-300">
                  <strong>Recipients:</strong> {alertSettings?.recipients || 'All admin users'}
                </p>
                <p className="text-sm text-slate-300 mt-1">
                  <strong>Frequency:</strong> {alertSettings?.frequency || 'Immediate + Weekly digest'}
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      
      {/* Alert History */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <History className="h-5 w-5 text-cyan-400" />
            Alert History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <Skeleton className="h-32" />
          ) : alertHistory?.alerts?.length > 0 ? (
            <div className="space-y-2">
              {alertHistory.alerts.map((alert, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-slate-800/50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-slate-400">
                      {new Date(alert.timestamp).toLocaleString()}
                    </p>
                    <Badge className={alert.acknowledged ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                      {alert.acknowledged ? 'Acknowledged' : 'Pending'}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    {alert.alerts?.map((a, i) => (
                      <p key={i} className="text-sm text-slate-300 flex items-start gap-2">
                        <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                          a.severity === 'high' ? 'text-rose-400' : 'text-amber-400'
                        }`} />
                        {a.message}
                      </p>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Sent to: {alert.recipients?.join(', ')}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-400 text-center py-4">No alerts sent yet</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Main Component
export default function AdvancedTrainingAlerts({ token }) {
  const [activeTab, setActiveTab] = useState('health');
  
  const { data: modelHealth, loading: healthLoading, refetch: refetchHealth } = useApiData(
    '/api/labor-utilization/advanced-ai/model-health'
  );
  
  return (
    <div className="space-y-6" data-testid="advanced-training-alerts">
      {/* Header */}
      <Alert className="bg-gradient-to-r from-violet-900/40 to-cyan-900/40 border-violet-500/30">
        <Brain className="h-4 w-4 text-violet-400" />
        <AlertTitle className="text-violet-400 font-bold">Advanced Model Training & Email Alerts</AlertTitle>
        <AlertDescription className="text-violet-200 text-sm">
          Train the AI model with historical data and configure automated email alerts for PRN demand spikes.
        </AlertDescription>
      </Alert>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-2 p-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50">
          <TabsTrigger value="health" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Heart className="h-4 w-4" /> Model Health
          </TabsTrigger>
          <TabsTrigger value="training" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Brain className="h-4 w-4" /> Training
          </TabsTrigger>
          <TabsTrigger value="alerts" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Mail className="h-4 w-4" /> Email Alerts
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="health" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ModelHealthCard health={modelHealth} loading={healthLoading} />
            
            {/* Recommendations */}
            {modelHealth?.recommendations?.length > 0 && (
              <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
                <CardHeader>
                  <CardTitle className="text-white font-bold flex items-center gap-2">
                    <Zap className="h-5 w-5 text-amber-400" />
                    Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {modelHealth.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-300">
                        <ChevronRight className="h-4 w-4 text-amber-400 mt-1 flex-shrink-0" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
        
        <TabsContent value="training" className="mt-6">
          <TrainingPanel token={token} onTrainingComplete={refetchHealth} />
        </TabsContent>
        
        <TabsContent value="alerts" className="mt-6">
          <EmailAlertsPanel token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
